/**
 * Upload a document into a new knowledge source, exactly as Knowledge → Files does
 * (pages/static-knowledge/data-source-connectors/file-modal): get an upload URL for the
 * source's metadata, PUT the file, then register the data source. Runs with the signed-in
 * builder's own session on the application they have open — the copilot never sees the file.
 */
import { dataSourceCreate, fileUpload, uploadUrlGet } from '../../../services/knowledge/ds-connector/file-connector';

export const MAX_UPLOAD_BYTES = 50 * 1024 * 1024;

export function fileExtension(name) {
    const parts = String(name || '').toLowerCase().split('.');
    return parts.length > 1 ? parts.pop() : '';
}

/** Why a file cannot be uploaded for this request, or null. */
export function fileProblem(file, accept) {
    if (!file) return 'Choose a file first.';
    const extension = fileExtension(file.name);
    if (!accept.includes(extension)) return `This source takes ${accept.map(e => `.${e}`).join(', ')} files.`;
    if (file.size > MAX_UPLOAD_BYTES) return 'The file is larger than 50 MB.';
    return null;
}

export async function uploadKnowledgeFile(appId, request, file) {
    const problem = fileProblem(file, request.accept);
    if (problem) throw new Error(problem);
    const extension = fileExtension(file.name);
    const metadata = {
        dataSourceId: request.dataSourceId,
        dataSourceType: 'file',
        dataSourceUrl: '',
        dataType: extension,
        idField: '',
        searchField: '',
        descriptionForModel: request.description,
        returnOriginal: false,
        ocr: { enable: Boolean(request.ocr), provider: 'default' },
    };

    const { response, status } = await uploadUrlGet(appId, { fileExt: extension, metadata });
    if (status !== 200 || !response?.uploadUrl) {
        throw new Error('Could not start the upload. Check that the source id is still free and try again.');
    }
    const uploaded = await fileUpload(response.uploadUrl, file);
    if (!uploaded || !uploaded.ok) throw new Error('The file upload failed. Try again.');
    const created = await dataSourceCreate(appId, response.metadata || metadata);
    if (created?.status && created.status >= 300) {
        throw new Error('The file uploaded but the knowledge source could not be registered. Try again.');
    }
    return { dataSourceId: request.dataSourceId, fileName: file.name };
}
